import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getDataLayerUtility } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import ProductListActions from "@tCentro/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@tCentro/Overrides/Widgets/ProductList/ProductListProductImage";
import { COLORS } from "@tCentro/theme/design";
import CENProductListActions from "@tCentro/Widgets/Product/CENProductListActions";
import CENProductListPriceCart from "@tCentro/Widgets/Product/CENProductListPriceCart";
import CENProductListProductCompare from "@tCentro/Widgets/Product/CENProductListProductCompare";
import CENProductListProductInformation from "@tCentro/Widgets/Product/CENProductListProductInformation";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const productsDataView = getProductListDataView(state);

    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        path: getSelectedCategoryPath(state) || getLocation(state).pathname,
        originalQuery: productsDataView.value ? productsDataView.originalQuery : undefined,
    };
};

type Props = ProductCardSelections & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    priceCartWrapper?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 16px 8px;
            min-width: 320px; /* prevent overlap with second column */
            display: flex;
            background: ${COLORS.background.grayShade} !important;
            flex-direction: column;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            div[class^="GridItemStyle"] {
                padding: 5px !important;
            }
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding: 15px 5px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding: 4px 8px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceCartWrapper: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {

    const onClickHandler = (event: React.MouseEvent) => {
        getDataLayerUtility({
            product: props.product,
            pricing: props.productInfo.pricing,
            quantity: props.productInfo.qtyOrdered || 1,
            breadcrumbs: props.breadcrumbLinks,
            originalQuery: props.originalQuery,
        });
    }

    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper>
                <CENProductListProductCompare showCompare={props.showCompare} showAddToList={props.showAddToList} />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <GridItem {...styles.leftColumnGridItem}>
                    {props.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                    <ProductListProductImage showImage={props.showImage} showCompare={props.showCompare} onClick={onClickHandler} />
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.rightColumnGridContainer}>
                        <GridItem {...styles.infoGridItem}>
                            <CENProductListProductInformation
                                showBrand={props.showBrand}
                                showTitle={props.showTitle}
                                showPartNumbers={props.showPartNumbers}
                                showAvailability={props.showAvailability}
                                showAttributes={props.showAttributes}
                                showPrice={props.showPrice}
                                onClick={onClickHandler}
                            />
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <CENProductListActions />
                        </GridItem>
                        <StyledWrapper {...styles.priceCartWrapper}>
                            <CENProductListPriceCart showPrice={props.showPrice} />
                        </StyledWrapper>
                        <GridItem {...styles.searchScoreDataGridItem}>
                            <ProductSearchScoreData />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductCard));
